import { motion } from 'framer-motion';
import { UserCheck, User, Phone, Mail, Calendar } from 'lucide-react';
import { OrderFormData } from '@/types/order';
import { Input } from '@/components/ui/input';

interface Step5ContactProps {
  data: OrderFormData;
  onChange: (data: Partial<OrderFormData>) => void;
}

export function Step5Contact({ data, onChange }: Step5ContactProps) {
  const isVide = data.occupation === 'vide';

  // Si le bien est vide, afficher un message informatif
  if (isVide) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-8"
      >
        <div>
          <h3 className="text-3xl font-display font-bold text-navy-600 mb-2">
            Contact occupant
          </h3>
          <p className="text-lg text-navy-400">Informations sur l'occupant</p>
        </div>

        {/* Message informatif pour bien vide */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center py-16 px-6 bg-cream rounded-2xl border-2 border-navy-100"
        >
          <div className="w-20 h-20 rounded-full bg-gold-100 flex items-center justify-center mb-6">
            <UserCheck className="w-10 h-10 text-gold-600" />
          </div>

          <h4 className="text-2xl font-display font-bold text-navy-600 mb-3">
            Bien inoccupé
          </h4>

          <p className="text-center text-navy-400 max-w-md leading-relaxed">
            Aucun contact occupant n'est nécessaire pour ce bien vide.
            <br />
            Vous pouvez passer à l'étape suivante.
          </p>
        </motion.div>
      </motion.div>
    );
  }

  // Si le bien est occupé, afficher le formulaire de contact
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div>
        <h3 className="text-3xl font-display font-bold text-navy-600 mb-2">
          Contact occupant
        </h3>
        <p className="text-lg text-navy-400">
          Coordonnées du {data.occupation === 'locataire' ? 'locataire' : 'propriétaire'}
        </p>
      </div>

      <div className="p-6 bg-amber-50 border border-amber-200 rounded-xl">
        <div className="flex items-start gap-3">
          <UserCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900 mb-1">
              Contact indispensable
            </p>
            <p className="text-sm text-amber-700">
              Ces informations sont nécessaires pour coordonner les visites avec l'occupant du bien.
            </p>
          </div>
        </div>
      </div>

      {/* Nom & Prénom */}
      <div>
        <label htmlFor="occupantNom" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <User className="w-4 h-4 text-gold-600" />
          Nom & Prénom *
        </label>
        <Input
          id="occupantNom"
          type="text"
          required
          value={data.occupantNom}
          onChange={(e) => onChange({ occupantNom: e.target.value })}
          placeholder="Nom et prénom de l'occupant"
        />
      </div>

      {/* Téléphone & Email */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="occupantTelephone" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
            <Phone className="w-4 h-4 text-gold-600" />
            GSM *
          </label>
          <Input
            id="occupantTelephone"
            type="tel"
            required
            value={data.occupantTelephone}
            onChange={(e) => onChange({ occupantTelephone: e.target.value })}
            placeholder="+32 XXX XX XX XX"
          />
        </div>

        <div>
          <label htmlFor="occupantEmail" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
            <Mail className="w-4 h-4 text-gold-600" />
            Email (optionnel)
          </label>
          <Input
            id="occupantEmail"
            type="email"
            value={data.occupantEmail}
            onChange={(e) => onChange({ occupantEmail: e.target.value })}
            placeholder="email@exemple.be"
          />
        </div>
      </div>

      {/* Disponibilités générales */}
      <div>
        <label htmlFor="disponibilitesGenerales" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <Calendar className="w-4 h-4 text-gold-600" />
          Disponibilités générales (optionnel)
        </label>
        <p className="text-sm text-navy-400 mb-3">
          Jours et horaires où l'occupant est généralement disponible
        </p>
        <textarea
          id="disponibilitesGenerales"
          rows={3}
          value={data.disponibilitesGenerales}
          onChange={(e) => onChange({ disponibilitesGenerales: e.target.value })}
          placeholder="Ex: Disponible en semaine après 18h et le weekend. Éviter les mardis..."
          className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-gold-500 focus:outline-none focus:ring-4 focus:ring-gold-500/20 transition-all resize-none text-navy-600 placeholder:text-navy-300"
        />
      </div>
    </motion.div>
  );
}
